/**
 * Active Stores.
 *
 * The branches trading with Deeliver, laid out with the same columns Vaperite
 * send us in "Store_Information_…xlsx" — name, physical address, province,
 * trading hours, email, contact number — so the two can be read side by side
 * without translating between them.
 */

import { useMemo, useState } from 'react';
import { Box, Paper, TextField, InputAdornment, Chip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useAdminTheme } from '../../admin-theme';
import { ACTIVE_VAPERITE_STORES, type VaperiteStore } from '../../data/vaperiteStores';

export const ActiveStoresTable = () => {
  const { colors, cardStyle } = useAdminTheme();
  const [search, setSearch] = useState('');

  const stores = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ACTIVE_VAPERITE_STORES;
    return ACTIVE_VAPERITE_STORES.filter((s) =>
      [s.branch, s.address, s.city, s.province, s.zip, s.phone, s.email]
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [search]);

  const headCell = {
    textAlign: 'left' as const,
    padding: '12px 16px',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    color: colors.textMuted,
    borderBottom: `1px solid ${colors.border}`,
    whiteSpace: 'nowrap' as const,
  };

  const cell = {
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.border}`,
    color: colors.textPrimary,
    fontSize: '0.85rem',
    verticalAlign: 'top' as const,
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
          {stores.length} of {ACTIVE_VAPERITE_STORES.length} trading branches
        </Typography>
        <TextField
          size="small"
          placeholder="Search branch, city, phone or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 300,
            bgcolor: colors.bgElevated,
            '& .MuiInputBase-input': { color: colors.textPrimary },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
          }}
        />
      </Box>

      <Paper sx={{ ...cardStyle, borderRadius: 2, overflowX: 'auto', maxWidth: '100%' }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={headCell}>Name</Box>
              <Box component="th" sx={headCell}>Physical Address</Box>
              <Box component="th" sx={headCell}>Province</Box>
              <Box component="th" sx={headCell}>Trading Hours</Box>
              <Box component="th" sx={headCell}>Email</Box>
              <Box component="th" sx={headCell}>Store Contact Nr</Box>
            </Box>
          </Box>

          <Box component="tbody">
            {stores.length === 0 ? (
              <Box component="tr">
                <Box component="td" colSpan={6} sx={{ ...cell, textAlign: 'center', py: 6, color: colors.textMuted }}>
                  No branches match that search.
                </Box>
              </Box>
            ) : (
              stores.map((store: VaperiteStore) => (
                <Box component="tr" key={store.branch} sx={{ '&:hover': { bgcolor: colors.bgElevated } }}>
                  <Box component="td" sx={{ ...cell, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {store.branch}
                  </Box>

                  <Box component="td" sx={{ ...cell, color: colors.textSecondary, minWidth: 300 }}>
                    {store.address}, {store.city}, {store.zip}
                  </Box>

                  <Box component="td" sx={cell}>
                    <Chip
                      size="small"
                      label={store.province}
                      sx={{ bgcolor: `${colors.info}1a`, color: colors.info, fontSize: '0.7rem', height: 22 }}
                    />
                  </Box>

                  {/* One line per day range, mirroring how Vaperite supply them. */}
                  <Box component="td" sx={{ ...cell, whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                    {store.tradingHours?.map((slot) => (
                      <Box key={slot.days} sx={{ display: 'flex', gap: 1, lineHeight: 1.7 }}>
                        <Box component="span" sx={{ color: colors.textMuted, minWidth: 62 }}>
                          {slot.days}
                        </Box>
                        <Box component="span" sx={{ fontFamily: 'monospace' }}>
                          {slot.open}–{slot.close}
                        </Box>
                      </Box>
                    )) ?? <Box component="span" sx={{ color: colors.textMuted }}>—</Box>}
                  </Box>

                  <Box component="td" sx={{ ...cell, whiteSpace: 'nowrap' }}>
                    <Box component="a" href={`mailto:${store.email}`} sx={{ color: colors.info, textDecoration: 'none' }}>
                      {store.email}
                    </Box>
                  </Box>

                  <Box component="td" sx={{ ...cell, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                    <Box component="a" href={`tel:${store.phone}`} sx={{ color: colors.textPrimary, textDecoration: 'none' }}>
                      {store.phone}
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ActiveStoresTable;
